/**
 * The ONE way a save copies the EMT's own data to their public profile (publicProfiles/{uid},
 * what employers search and see).
 *
 * The copy is a separate write after the user's own save. It can fail (offline, a rules
 * rejection, a slow geocode) while the save itself succeeded. Every caller used to ignore that,
 * so the person was told "Saved" while employers kept seeing old details. Now:
 *   - the user's own save is never blocked or undone (this never throws);
 *   - on failure the user is told plainly ("Saved. Your public profile did not update. We will
 *     retry.") with a "Retry now" button, through PublicProfileSyncNotices (mounted once, app-wide);
 *   - it retries on its own a couple of times; a later save also retries.
 *
 * Read-only previews (publicProfileRepo.ensureMyPublicProfile / getOwnerPreviewProfile) call the
 * quiet syncPublicProfileDetailed instead: they are not a save, so "Saved" would be wrong there.
 */
// Type-only: the sync code (geocoding, readiness inputs) is loaded on first use, so mounting the
// notices app-wide does not pull it into the first-load bundle.
import type { PublicProfileSyncOutcome } from './firestore/publicProfiles';

export type PublicProfileSyncNotice =
  /** A save's copy failed; automatic retries are scheduled. */
  | { kind: 'failed'; uid: string }
  /** A retry the user asked for worked. */
  | { kind: 'recovered'; uid: string }
  /** Every automatic retry failed too. */
  | { kind: 'gaveUp'; uid: string };

type Listener = (notice: PublicProfileSyncNotice) => void;

const listeners = new Set<Listener>();

/** Automatic retries after a failed copy (ms after the failure). */
export const PUBLIC_PROFILE_RETRY_DELAYS_MS = [4_000, 15_000];

/** One retry chain per uid, so a burst of failed saves does not stack timers or toasts. */
const retryChains = new Map<string, { timer: ReturnType<typeof setTimeout> | null; attempt: number }>();

function emit(notice: PublicProfileSyncNotice) {
  listeners.forEach((l) => {
    try {
      l(notice);
    } catch {
      // A broken listener must never break a save.
    }
  });
}

export function subscribePublicProfileSyncNotices(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function runSync(uid: string): Promise<PublicProfileSyncOutcome> {
  try {
    const { syncPublicProfileDetailed } = await import('./firestore/publicProfiles');
    return await syncPublicProfileDetailed(uid);
  } catch (err) {
    console.warn('[publicProfileSync] sync threw:', err);
    return 'failed';
  }
}

function cancelChain(uid: string) {
  const chain = retryChains.get(uid);
  if (chain?.timer) clearTimeout(chain.timer);
  retryChains.delete(uid);
}

function scheduleRetry(uid: string) {
  const chain = retryChains.get(uid) ?? { timer: null, attempt: 0 };
  if (chain.timer) clearTimeout(chain.timer);
  const delay = PUBLIC_PROFILE_RETRY_DELAYS_MS[chain.attempt];
  if (delay === undefined) {
    retryChains.delete(uid);
    emit({ kind: 'gaveUp', uid });
    return;
  }
  chain.timer = setTimeout(async () => {
    chain.timer = null;
    chain.attempt += 1;
    const outcome = await runSync(uid);
    if (outcome === 'failed') scheduleRetry(uid);
    else retryChains.delete(uid);
  }, delay);
  retryChains.set(uid, chain);
}

/**
 * Copy the EMT's saved profile to their public profile after a save. Never throws and never
 * blocks on retries: returns the first attempt's outcome ('skipped' is normal, e.g. sign-up not
 * finished). On 'failed' the user is told once and retries are scheduled.
 */
export async function syncPublicProfileAfterSave(uid: string): Promise<PublicProfileSyncOutcome> {
  if (!uid) return 'skipped';
  const outcome = await runSync(uid);
  if (outcome !== 'failed') {
    // A newer save went through, so any pending retry for an older one is moot.
    cancelChain(uid);
    return outcome;
  }
  const alreadyRetrying = retryChains.has(uid);
  // A fresh failure restarts the retry schedule; the user is told only once per chain.
  if (alreadyRetrying) cancelChain(uid);
  scheduleRetry(uid);
  if (!alreadyRetrying) emit({ kind: 'failed', uid });
  return outcome;
}

/**
 * For a sign-up step that navigates right after saving: wait for the copy (so closing the tab
 * straight after does not leave a blank public profile), but never longer than `waitAtMostMs`.
 * On timeout the copy keeps going in the background and still reports a failure app-wide.
 */
export async function syncPublicProfileBeforeLeaving(
  uid: string,
  waitAtMostMs = 8000
): Promise<PublicProfileSyncOutcome | 'stillSaving'> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<'stillSaving'>((resolve) => {
    timer = setTimeout(() => resolve('stillSaving'), waitAtMostMs);
  });
  try {
    return await Promise.race([syncPublicProfileAfterSave(uid), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** "Retry now" from the notice: one immediate attempt; on failure the automatic retries carry on. */
export async function retryPublicProfileSyncNow(uid: string): Promise<PublicProfileSyncOutcome> {
  const outcome = await runSync(uid);
  if (outcome !== 'failed') {
    cancelChain(uid);
    emit({ kind: 'recovered', uid });
  } else if (!retryChains.has(uid)) {
    scheduleRetry(uid);
  }
  return outcome;
}

/** Test seam: forget all retry chains and listeners. */
export function __resetPublicProfileSyncFeedbackForTests() {
  retryChains.forEach((c) => c.timer && clearTimeout(c.timer));
  retryChains.clear();
  listeners.clear();
}
